// src/components/feedback/FeedbackModal.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaStar, FaPaperPlane, FaComment, FaCheckCircle, FaThumbsUp } from 'react-icons/fa';
import { useBranch } from '~/contexts/BranchContext';
import type { MenuItem } from '~/types/new_types';
import { 
  submitUserFeedback, 
  getSessionId, 
  convertStarRatingToNormalized,
  type FeedbackData 
} from '~/services/feedbackService';
import { getItemName } from '~/utils/menuItemHelper';

interface FeedbackModalProps {
  show: boolean;
  onClose: () => void;
  item?: MenuItem; // If provided, feedback is for the item; otherwise for branch
  onSuccess?: () => void; // Called when feedback is successfully submitted
  initialRating?: number;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ show, onClose, item, onSuccess, initialRating = 0 }) => {
  const { t, i18n } = useTranslation();
  const { branch } = useBranch();
  const [rating, setRating] = useState(initialRating);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const isRtl = i18n.dir() === 'rtl';
  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setRating(initialRating);
      setHoverRating(0);
      setFeedback('');
      setSubmitStatus('idle');
    }
  }, [show, initialRating]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      return; // Rating is required
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const feedbackData: FeedbackData = {
        session_id: getSessionId(),
        branch_id: branch?.id || '',
        feedback: feedback.trim(),
        normalized_rating: convertStarRatingToNormalized(rating),
        ...(item && { item_id: item.id })
      };

      const result = await submitUserFeedback(feedbackData);

      if (result.success) {
        setSubmitStatus('success');
        // Close modal and trigger success callback
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 500);
      } else {
        setSubmitStatus('error');
        console.error('Failed to submit feedback:', result.error);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
  };

  const handleStarHover = (starRating: number) => {
    setHoverRating(starRating);
  };

  const handleStarLeave = () => {
    setHoverRating(0);
  };

  if (!show || !branch) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
          style={{
            backgroundColor: branch.theme.cardColor,
            borderRadius: branch.theme.borderRadius,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="p-6 relative "
            style={{ 
              background: `linear-gradient(135deg, ${branch.theme.primaryColor}15, ${branch.theme.primaryColor}05)` 
            }}
          >
            <button
              onClick={onClose}
              className={`absolute top-4 p-2 rounded-full cursor-pointer hover:bg-black/10 transition-colors ${isRtl ? 'left-4' : 'right-4'}`}
              style={{ color: branch.theme.textColor }}
            >
              <FaTimes size={16} />
            </button>
            
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ 
                  backgroundColor: `${branch.theme.primaryColor}20`,
                  color: branch.theme.primaryColor 
                }}
              >
                <FaStar size={20} />
              </div>
              <div>
                <h2 
                  className="text-xl font-bold mb-1"
                  style={{ color: branch.theme.textColor }}
                >
                  {item ? t('feedback.itemTitle') : t('feedback.branchTitle')}
                </h2>
                {item && (
                  <p 
                    className="text-sm opacity-70"
                    style={{ color: branch.theme.textColor }}
                  >
                    {getItemName(item, i18n.language)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Star Rating */}
            <div className="text-center">
              <p 
                className="text-sm font-medium mb-4"
                style={{ color: branch.theme.textColor }}
              >
                {t('feedback.ratingLabel')}
              </p>
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="p-1 transition-transform hover:scale-110"
                    onClick={() => handleStarClick(star)}
                    onMouseEnter={() => handleStarHover(star)}
                    onMouseLeave={handleStarLeave}
                  >
                    <FaStar
                      size={24}
                      className={
                        star <= (hoverRating || rating)
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm mt-2"
                  style={{ color: `${branch.theme.textColor}80` }}
                >
                  {rating} {rating === 1 ? t('feedback.star') : t('feedback.stars')}
                </motion.p>
              )}
            </div>

            {/* Feedback Text */}
            <div>
              <label 
                htmlFor="feedback-text"
                className="block text-sm font-medium mb-3"
                style={{ color: branch.theme.textColor }}
              >
                {t('feedback.commentLabel')} {t('feedback.optional')}
              </label>
              <textarea
                id="feedback-text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={item ? t('feedback.itemPlaceholder') : t('feedback.branchPlaceholder')}
                rows={4}
                className="w-full p-3 border rounded-lg resize-none transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: `${branch.theme.primaryColor}05`,
                  borderColor: `${branch.theme.primaryColor}30`,
                  color: branch.theme.textColor,
                  borderRadius: branch.theme.borderRadius,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = branch.theme.primaryColor;
                  e.target.style.boxShadow = `0 0 0 2px ${branch.theme.primaryColor}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = `${branch.theme.primaryColor}30`;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={rating === 0 || isSubmitting}
              className="w-full p-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: branch.theme.primaryColor,
                color: '#fff',
                borderRadius: branch.theme.borderRadius,
              }}
              whileHover={{ scale: rating > 0 && !isSubmitting ? 1.02 : 1 }}
              whileTap={{ scale: rating > 0 && !isSubmitting ? 0.98 : 1 }}
            >
              {isSubmitting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                />
              ) : (
                <>
                  <FaPaperPlane size={16} />
                  {t('feedback.submit')}
                </>
              )}
            </motion.button>

            {/* Status Messages */}
            <AnimatePresence>
              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-center p-3 rounded-lg bg-red-50 text-red-700 text-sm"
                  style={{ borderRadius: branch.theme.borderRadius }}
                >
                  {t('feedback.error')}
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeedbackModal;